<template>
    <Head :title="campaign ? `${campaign.name} — Admin Report` : 'Campaign Report'" />
    <BaseLayout gap="none" full-bleed-header>
        <template #header>
            <header class="border-b border-valence-grey-200 bg-white px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                <a href="/reflections/admin" class="body-small text-valence-grey-750 hover:text-valence-grey-850 flex items-center gap-1">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> Admin
                </a>
                <div class="flex items-center gap-2">
                    <AssessmentEyebrow>Campaign report</AssessmentEyebrow>
                    <BaseButton v-if="report" theme="neutral" size="sm" :disabled="downloading" class="inline-flex items-center gap-2" @click="onDownloadPdf">
                        <i class="bi bi-download" aria-hidden="true"></i>
                        {{ downloading ? "Downloading…" : "Download PDF" }}
                    </BaseButton>
                    <BaseButton theme="neutral" size="sm" :disabled="downloadingCsv" class="inline-flex items-center gap-2" @click="onDownloadCsv">
                        <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                        {{ downloadingCsv ? "Downloading…" : "Download CSV" }}
                    </BaseButton>
                    <BaseButton theme="neutral" size="sm" :disabled="regenerating" class="inline-flex items-center gap-2" @click="onRegenerate">
                        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                        {{ regenerating ? "Queued…" : "Regenerate report" }}
                    </BaseButton>
                </div>
            </header>
        </template>

        <BaseNotification v-if="regenStatus" :theme="regenStatus.theme" :text="regenStatus.text" class="mb-6" @dismiss="regenStatus = null" />
        <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <template v-else-if="campaign">
            <!-- Title -->
            <section>
                <BasePageHeader :title="campaign.name">
                    <template #subtitle>
                        {{ campaign.template.name }} · <b>{{ campaign.cycle_count }} participants</b> · {{ campaign.submitted_response_count }} responses submitted
                    </template>
                </BasePageHeader>
                <div v-if="report?.generated_at" class="details text-valence-grey-700 mt-1">
                    Report generated {{ formatDate(report.generated_at) }}
                    <span v-if="newSubmissionsSinceReport > 0" class="text-valence-gold-600 font-medium">
                        · {{ newSubmissionsSinceReport }} new submissions since last report
                    </span>
                </div>
            </section>

            <div class="mt-12 flex flex-col gap-12">
                <!-- Empty state if no report yet -->
                <BaseCard v-if="!report" theme="muted" radius="lg" padding="lg" class="border-dashed border-valence-grey-300 text-center">
                    <p class="body-small text-valence-grey-750">No report generated yet. Click "Regenerate report" to generate one.</p>
                </BaseCard>

                <template v-else>
                    <ExecutiveSummarySection v-if="report.executive_summary" :summary="report.executive_summary" />

                    <CategoryScoresSection v-if="aggregates && hasCategoryScores" :sections="aggregates.sections ?? []" :self-assess-enabled="false" :max-score="defaultMaxScore" />

                    <section v-if="availableFilters.length > 0">
                        <div class="label-section mb-3">Filter by rater</div>
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="opt in availableFilters"
                                :key="opt.value"
                                type="button"
                                class="px-3 py-1 rounded-full border body-small transition-colors"
                                :class="
                                    relationshipFilter === opt.value
                                        ? 'border-valence-primary-500 bg-valence-primary-500 text-white'
                                        : 'border-valence-grey-300 bg-white text-valence-grey-850 hover:bg-valence-grey-100'
                                "
                                @click="relationshipFilter = opt.value">
                                {{ opt.label }}{{ opt.count !== null ? ` (${opt.count})` : "" }}
                            </button>
                        </div>
                        <p v-if="relationshipFilter !== 'all'" class="details text-valence-grey-700 mt-2">
                            Distributions and averages reflect the selected raters only. Executive summary and themes always reflect the full set.
                        </p>
                    </section>

                    <SectionResultsPanel
                        v-for="panel in sectionPanels"
                        :key="panel.section.section_id"
                        :section="panel.section"
                        :questions="panel.questions"
                        :subsections="panel.subsections" />

                    <section v-if="aggregates && (hasCategoryScores || filteredScoreQuestions.length > 0)" class="flex flex-col gap-10">
                        <h2 class="title-section text-valence-grey-850">Detailed breakdown</h2>
                        <PerSectionDistributionSection v-if="hasCategoryScores" :sections="filteredSections" />
                        <PerQuestionDistributionSection v-if="filteredScoreQuestions.length > 0" :questions="filteredScoreQuestions" />
                    </section>
                </template>
            </div>

            <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
        </template>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { fetchApi, useFetch } from "~vue/api";
import type { CampaignAdminView, CampaignReportPayload, CampaignResultsResponse } from "~vue/api/generated/reflections";
import CategoryScoresSection from "~vue/AssessmentResults/CategoryScoresSection.vue";
import ExecutiveSummarySection from "~vue/AssessmentResults/ExecutiveSummarySection.vue";
import PerQuestionDistributionSection from "~vue/AssessmentResults/PerQuestionDistributionSection.vue";
import PerSectionDistributionSection from "~vue/AssessmentResults/PerSectionDistributionSection.vue";
import SectionResultsPanel from "~vue/AssessmentResults/SectionResultsPanel.vue";
import type { Aggregates, Cut, PerQuestion, SectionAggregate } from "~vue/AssessmentResults/types";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref } from "vue";

const props = defineProps<{
    campaign_id: string;
}>();

const campaign = ref<CampaignAdminView | null>(null);
const report = ref<CampaignReportPayload | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const regenerating = ref(false);
const downloading = ref(false);
const downloadingCsv = ref(false);
const regenStatus = ref<{ theme: "success" | "info" | "error"; text: string } | null>(null);
const campaignDemoActions = computed(() => {
    const currentCampaign = campaign.value;
    return currentCampaign && currentCampaign.state !== "closed" ? [{ id: currentCampaign.id, label: currentCampaign.name }] : [];
});

const aggregates = computed<Aggregates | null>(() => report.value?.aggregates ?? null);
const scoreQuestions = computed<PerQuestion[]>(() => aggregates.value?.per_question?.filter((q) => q.is_score) ?? []);
const hasCategoryScores = computed(() => (aggregates.value?.sections ?? []).some((s) => s.avg !== null));

const relationshipFilter = ref<string>("all");

// Which relationship cuts have data available across the campaign? We derive
// the option list from the section-level cuts (which already enforce anonymity
// via question-level rollup into Combined).
const availableFilters = computed<{ value: string; label: string; count: number | null }[]>(() => {
    const sections = aggregates.value?.sections ?? [];
    const totals = new Map<string, { label: string; count: number }>();
    for (const s of sections) {
        for (const cut of s.cuts ?? []) {
            const cur = totals.get(cut.relationship);
            if (cur) {
                cur.count += cut.count;
            } else {
                totals.set(cut.relationship, { label: cut.label, count: cut.count });
            }
        }
    }
    const opts: { value: string; label: string; count: number | null }[] = [{ value: "all", label: "All", count: null }];
    for (const [value, { label, count }] of totals) {
        if (count > 0) {
            opts.push({ value, label, count });
        }
    }
    return opts.length > 1 ? opts : [];
});

function applyCutToQuestion(q: PerQuestion): PerQuestion {
    if (relationshipFilter.value === "all") {
        return q;
    }
    const cut: Cut | undefined = q.cuts?.find((c) => c.relationship === relationshipFilter.value);
    if (!cut || !cut.distribution) {
        return { ...q, all_avg: null, all_count: 0, distribution: (q.distribution ?? []).map(() => 0) };
    }
    return {
        ...q,
        all_avg: cut.avg,
        all_count: cut.count,
        distribution: cut.distribution,
    };
}

function applyCutToSection(s: SectionAggregate): SectionAggregate {
    if (relationshipFilter.value === "all") {
        return s;
    }
    const cut = (s.cuts ?? []).find((c) => c.relationship === relationshipFilter.value);
    if (!cut || !cut.distribution) {
        return { ...s, avg: null, count: 0, distribution: (s.distribution ?? []).map(() => 0) };
    }
    return {
        ...s,
        avg: cut.avg,
        count: cut.count,
        distribution: cut.distribution,
    };
}

const filteredSections = computed<SectionAggregate[]>(() => (aggregates.value?.sections ?? []).map(applyCutToSection));
const filteredScoreQuestions = computed<PerQuestion[]>(() => scoreQuestions.value.map(applyCutToQuestion));

// All questions (both score + text), keyed by section_id, with relationship
// filter applied to score questions. Text questions pass through unchanged
// — themes are derived from full open_text and the filter doesn't reshape
// them per relationship in this iteration.
const allQuestionsFiltered = computed<PerQuestion[]>(() => {
    const all = aggregates.value?.per_question ?? [];
    return all.map((q) => (q.is_score ? applyCutToQuestion(q) : q));
});

interface SectionPanel {
    section: SectionAggregate;
    questions: PerQuestion[];
    subsections: SectionAggregate[];
}

// One panel per top-level section. Questions are pulled from all sections
// nested under that top-level (so sub-sections roll up into the panel). The
// panel also receives the list of direct child subsections so it can render
// per-subsection bands (e.g. Strategize / Inspire / Deliver under UPS
// Leadership Effectiveness).
const sectionPanels = computed<SectionPanel[]>(() => {
    const allSections = filteredSections.value;
    if (allSections.length === 0) {
        return [];
    }
    const topLevel = allSections.filter((s) => s.depth === 0).sort((a, b) => a.order - b.order);
    const childrenByParent = new Map<string, SectionAggregate[]>();
    for (const s of allSections) {
        if (s.parent_section_id) {
            const list = childrenByParent.get(s.parent_section_id) ?? [];
            list.push(s);
            childrenByParent.set(s.parent_section_id, list);
        }
    }
    return topLevel.map((section) => {
        const subsections = childrenByParent.get(section.section_id) ?? [];
        const sectionIds = new Set<string>([section.section_id, ...subsections.map((s) => s.section_id)]);
        return {
            section,
            subsections,
            questions: allQuestionsFiltered.value.filter((q) => q.section_id && sectionIds.has(q.section_id)),
        };
    });
});
const defaultMaxScore = computed(() => {
    const first = scoreQuestions.value[0];
    return first?.max_score ?? first?.distribution?.length ?? 5;
});

const newSubmissionsSinceReport = computed(() => {
    if (!campaign.value || !report.value) {
        return 0;
    }
    return Math.max(0, (campaign.value.submitted_response_count ?? 0) - (report.value.response_count_at_generation ?? 0));
});

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CAMPAIGN_PDF_DOWNLOAD_CLICKED, {
        campaign_id: props.campaign_id,
    });
    downloading.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/admin/campaigns/${props.campaign_id}/export-pdf`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? "campaign_report.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloading.value = false;
    }
}

async function onDownloadCsv() {
    if (downloadingCsv.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CAMPAIGN_CSV_DOWNLOAD_CLICKED, {
        campaign_id: props.campaign_id,
    });
    downloadingCsv.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/admin/campaigns/${props.campaign_id}/export-csv`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? "campaign_raw_data.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloadingCsv.value = false;
    }
}

async function fetchResults(): Promise<CampaignResultsResponse | null> {
    const { data } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaign_id}/results`).json<CampaignResultsResponse>();
    return data.value ?? null;
}

async function onRegenerate() {
    if (regenerating.value) {
        return;
    }
    regenerating.value = true;
    regenStatus.value = { theme: "info", text: "Regenerating report — this usually takes a few seconds…" };
    const baselineGeneratedAt = report.value?.generated_at ?? null;

    const { error: postError } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaign_id}/regenerate`, {
        method: "POST",
    }).json();
    if (postError.value) {
        regenerating.value = false;
        regenStatus.value = { theme: "error", text: "Could not queue regeneration. Please try again." };
        return;
    }

    // Poll until generated_at advances (or timeout). 18 × ~2s ≈ 36s budget.
    const startedAt = Date.now();
    for (let i = 0; i < 18; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const latest = await fetchResults();
        const newGenerated = latest?.report?.generated_at ?? null;
        if (newGenerated && newGenerated !== baselineGeneratedAt) {
            campaign.value = latest!.campaign;
            report.value = latest!.report ?? null;
            regenerating.value = false;
            regenStatus.value = {
                theme: "success",
                text: `Report regenerated (${((Date.now() - startedAt) / 1000).toFixed(0)}s).`,
            };
            return;
        }
    }
    regenerating.value = false;
    regenStatus.value = {
        theme: "info",
        text: "Regeneration is taking longer than expected. Refresh the page in a moment to see the updated report.",
    };
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaign_id}/results`).json<CampaignResultsResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load campaign results.";
        return;
    }
    campaign.value = data.value?.campaign ?? null;
    report.value = data.value?.report ?? null;
});
</script>
